import { useState, useEffect, useCallback, useId } from 'react';
import { TablePagination } from '@mui/material';
import { useFocusTrap } from '_components';
import * as api from './listsApi';
import './lists.css';

export { IpListDetail };

function timeAgo(iso) {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

const EXPORT_FORMATS = [
    { value: 'json',     label: 'JSON' },
    { value: 'plain',    label: 'Plain Text' },
    { value: 'csv',      label: 'CSV' },
    { value: 'nftables', label: 'nftables' },
    { value: 'ipset',    label: 'ipset' },
];

function IpListDetail({ listId, onBack }) {
    const addTitleId = useId();
    const bulkTitleId = useId();
    const addIpId = useId();
    const addMetaId = useId();
    const bulkIpsId = useId();
    const [list, setList] = useState(null);
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [loading, setLoading] = useState(true);
    const [entriesLoading, setEntriesLoading] = useState(false);
    const [error, setError] = useState(null);

    // Export dropdown
    const [exportOpen, setExportOpen] = useState(false);

    // Add entry dialog
    const [addOpen, setAddOpen] = useState(false);
    const [newIp, setNewIp] = useState('');
    const [newMeta, setNewMeta] = useState('');
    const [addError, setAddError] = useState(null);
    const [adding, setAdding] = useState(false);

    // Bulk add dialog
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkIps, setBulkIps] = useState('');
    const [bulkAdding, setBulkAdding] = useState(false);
    const [bulkError, setBulkError] = useState(null);
    const addModalRef = useFocusTrap(addOpen, () => setAddOpen(false));
    const bulkModalRef = useFocusTrap(bulkOpen, () => setBulkOpen(false));

    // Close modals on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (addOpen)  { setAddOpen(false);  return; }
            if (bulkOpen) { setBulkOpen(false);  return; }
            if (exportOpen) { setExportOpen(false); return; }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [addOpen, bulkOpen, exportOpen]);

    // Load list details
    useEffect(() => {
        setLoading(true);
        api.fetchIpList(listId)
            .then(data => {
                if (!data || data.error || data.message) {
                    setError(`IP list not found (ID: ${listId})`);
                } else {
                    setList(data);
                }
            })
            .catch(() => setError(`Failed to load IP list (ID: ${listId})`))
            .finally(() => setLoading(false));
    }, [listId]);

    // Load entries
    const loadEntries = useCallback(() => {
        setEntriesLoading(true);
        api.fetchIpListEntries(listId, { page: page + 1, page_size: pageSize })
            .then(r => { setEntries(r.data || []); setTotal(r.total || 0); })
            .catch(() => setError('Failed to load entries'))
            .finally(() => setEntriesLoading(false));
    }, [listId, page, pageSize]);

    useEffect(() => { loadEntries(); }, [loadEntries]);

    // Export handler
    const handleExport = async (format) => {
        setExportOpen(false);
        try {
            const res = await api.exportIpList(listId, format);
            const contentType = res.headers.get('content-type') || '';
            let filename = `${list?.name || 'ip-list'}.${format === 'plain' ? 'txt' : format}`;

            if (format === 'json') {
                const data = await res.json();
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                downloadBlob(blob, filename);
            } else {
                const text = await res.text();
                const blob = new Blob([text], { type: contentType || 'text/plain' });
                downloadBlob(blob, filename);
            }
        } catch {
            setError('Export failed');
        }
    };

    const downloadBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    // Add single entry
    const handleAddEntry = async () => {
        setAddError(null);
        setAdding(true);
        try {
            let metadata = {};
            if (newMeta.trim()) {
                metadata = JSON.parse(newMeta);
            }
            await api.addIpEntry(listId, { ip_address: newIp.trim(), metadata });
            setNewIp('');
            setNewMeta('');
            setAddOpen(false);
            loadEntries();
        } catch (e) {
            setAddError(e.message || 'Failed to add entry');
        }
        setAdding(false);
    };

    // Bulk add entries
    const handleBulkAdd = async () => {
        setBulkError(null);
        setBulkAdding(true);
        try {
            const ips = bulkIps.split('\n').map(l => l.trim()).filter(Boolean);
            if (ips.length === 0) { setBulkError('Enter at least one IP'); setBulkAdding(false); return; }
            const entriesPayload = ips.map(ip => ({ ip_address: ip, metadata: {} }));
            await api.bulkAddIpEntries(listId, entriesPayload);
            setBulkIps('');
            setBulkOpen(false);
            loadEntries();
        } catch (e) {
            setBulkError(e.message || 'Failed to add entries');
        }
        setBulkAdding(false);
    };

    // Delete entry
    const handleDeleteEntry = async (entryId) => {
        try {
            await api.deleteIpEntry(listId, entryId);
            loadEntries();
        } catch { setError('Failed to delete entry'); }
    };

    if (loading) return (
        <div className="lists-loading">
            <span className="material-symbols-outlined lists-spin" style={{ fontSize: '2rem', color: 'var(--color-accent-teal)' }}>sync</span>
        </div>
    );

    if (!list) return (
        <div className="lists-container">
            <div className="lists-error-banner">
                <span className="material-symbols-outlined text-sm">error</span>
                IP list not found.
                <button onClick={onBack} className="lists-modal-cancel lists-ml-auto">Go back</button>
            </div>
        </div>
    );

    const syncBadgeClass = !list.provider_id ? 'static' :
        list.sync_status === 'completed' ? 'synced' :
        list.sync_status === 'syncing'   ? 'syncing' :
        list.sync_status === 'failed'    ? 'failed' : 'pending';

    return (
        <div className="lists-container">
            {/* ── Hero ──────────────────────────────────────────── */}
            <div className="lists-hero">
                <div>
                    <div className="lists-breadcrumb">
                        <span className="lists-breadcrumb-dot"></span>
                        <span className="lists-breadcrumb-text">IP_LISTS / {list.name.toUpperCase()}</span>
                    </div>
                    <div className="lists-detail-title-row">
                        <button className="lists-icon-btn" onClick={onBack} title="Back">
                            <span className="material-symbols-outlined">arrow_back</span>
                        </button>
                        <h1 className="lists-title">{list.name}</h1>
                    </div>
                    {list.description && <p className="lists-tab-desc lists-detail-description">{list.description}</p>}
                </div>
                <div className="lists-detail-hero-actions">
                    <button className="lists-create-btn" onClick={() => setAddOpen(true)}>
                        <span className="material-symbols-outlined text-sm">add</span>
                        Add Entry
                    </button>
                    <button className="lists-create-btn" onClick={() => setBulkOpen(true)}>
                        <span className="material-symbols-outlined text-sm">playlist_add</span>
                        Bulk Add
                    </button>
                    <div className="lists-detail-export-wrap">
                        <button className="lists-create-btn" onClick={() => setExportOpen(v => !v)}>
                            <span className="material-symbols-outlined text-sm">download</span>
                            Export
                            <span className="material-symbols-outlined text-sm">expand_more</span>
                        </button>
                        {exportOpen && (
                            <div className="lists-export-menu">
                                {EXPORT_FORMATS.map(f => (
                                    <button key={f.value} onClick={() => handleExport(f.value)} className="lists-export-item">{f.label}</button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {error && (
                <div className="lists-error-banner">
                    <span className="material-symbols-outlined text-sm">error</span>
                    {error}
                    <button className="lists-inline-clear-btn" onClick={() => setError(null)}>
                        <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                </div>
            )}

            {/* ── Meta cards ────────────────────────────────────── */}
            <div className="lists-stats-grid lists-detail-stats-grid">
                <div className="lists-stat-card">
                    <div className="lists-stat-icon-row">
                        <span className="material-symbols-outlined lists-stat-icon">view_quilt</span>
                        <span className="lists-stat-label">Provider</span>
                    </div>
                    <div className="lists-stat-value lists-stat-value-compact">{list.provider?.display_name || '—'}</div>
                </div>
                <div className="lists-stat-card">
                    <div className="lists-stat-icon-row">
                        <span className="material-symbols-outlined lists-stat-icon">folder</span>
                        <span className="lists-stat-label">Category</span>
                    </div>
                    <div className="lists-stat-value lists-stat-value-compact">{list.category?.name || '—'}</div>
                </div>
                <div className="lists-stat-card">
                    <div className="lists-stat-icon-row">
                        <span className="material-symbols-outlined lists-stat-icon">public</span>
                        <span className="lists-stat-label">Entries</span>
                    </div>
                    <div className="lists-stat-value">{(list.entry_count ?? total)?.toLocaleString() ?? '—'}</div>
                </div>
                <div className="lists-stat-card">
                    <div className="lists-stat-icon-row">
                        <span className="material-symbols-outlined lists-stat-icon">toggle_on</span>
                        <span className="lists-stat-label">Active</span>
                    </div>
                    <div className="lists-stat-inline">
                        <span className={`lists-status-pill ${list.is_active ? 'active' : 'inactive'}`}>
                            {list.is_active ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                </div>
                <div className="lists-stat-card">
                    <div className="lists-stat-icon-row">
                        <span className="material-symbols-outlined lists-stat-icon">sync</span>
                        <span className="lists-stat-label">Sync Status</span>
                    </div>
                    <div className="lists-stat-inline">
                        <span className={`lists-status-badge ${syncBadgeClass}`}>
                            {!list.provider_id
                                ? <><span className="material-symbols-outlined text-base">edit_note</span> STATIC</>
                                : list.sync_status === 'completed'
                                    ? <><span className="material-symbols-outlined text-base" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span> SYNCED</>
                                    : list.sync_status === 'syncing'
                                        ? <><span className="material-symbols-outlined text-base lists-spin">sync</span> UPDATING</>
                                        : list.sync_status === 'failed'
                                            ? <><span className="material-symbols-outlined text-base" style={{ fontVariationSettings: "'FILL' 1" }}>error</span> FAILED</>
                                            : <><span className="material-symbols-outlined text-base">schedule</span> PENDING</>
                            }
                        </span>
                    </div>
                </div>
                <div className="lists-stat-card">
                    <div className="lists-stat-icon-row">
                        <span className="material-symbols-outlined lists-stat-icon">calendar_today</span>
                        <span className="lists-stat-label">Created</span>
                    </div>
                    <div className="lists-row-time lists-stat-inline-text">{new Date(list.created_at).toLocaleString()}</div>
                </div>
                <div className="lists-stat-card">
                    <div className="lists-stat-icon-row">
                        <span className="material-symbols-outlined lists-stat-icon">update</span>
                        <span className="lists-stat-label">Updated</span>
                    </div>
                    <div className="lists-row-time lists-stat-inline-text">{timeAgo(list.updated_at)}</div>
                </div>
            </div>

            {/* ── Entries table ──────────────────────────────────── */}
            {entriesLoading && (
                <div className="lists-progress-bar lists-progress-bar-gap">
                    <div className="lists-progress-fill"></div>
                </div>
            )}

            <div className="lists-table-card">
                <div className="lists-table-header">
                    <div className="lists-table-title-row">
                        <h2 className="lists-table-title">IP Entries</h2>
                        <span className="lists-badge secondary">{total.toLocaleString()} Total</span>
                    </div>
                </div>
                <div className="lists-table-wrap">
                    <table className="lists-table">
                        <thead>
                            <tr className="lists-table-head-row">
                                <th className="lists-th">IP Address</th>
                                <th className="lists-th">Version</th>
                                <th className="lists-th">Metadata</th>
                                <th className="lists-th">Added</th>
                                <th className="lists-th"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.length === 0 && !entriesLoading && (
                                <tr><td colSpan={5} className="lists-td lists-empty-cell">No entries in this list</td></tr>
                            )}
                            {entries.map(entry => {
                                const metaText = entry.metadata && Object.keys(entry.metadata).length > 0
                                    ? Object.entries(entry.metadata).map(([k, v]) => `${k}: ${v}`).join(', ')
                                    : null;
                                return (
                                    <tr key={entry.id} className="lists-table-row lists-table-row-static">
                                        <td className="lists-td">
                                            <span className="lists-row-name lists-row-name-mono">{entry.ip_address}</span>
                                        </td>
                                        <td className="lists-td">
                                            <span className={`lists-type-badge ${entry.ip_version === 6 ? 'external' : 'static'}`}>
                                                IPv{entry.ip_version}
                                            </span>
                                        </td>
                                        <td className="lists-td">
                                            {metaText
                                                                                                ? <span className="lists-row-desc lists-meta-help" title={JSON.stringify(entry.metadata, null, 2)}>
                                                    {metaText.length > 120 ? `${metaText.substring(0, 120)}…` : metaText}
                                                  </span>
                                                : <span className="lists-row-time">—</span>
                                            }
                                        </td>
                                        <td className="lists-td">
                                            <span className="lists-row-time">{timeAgo(entry.created_at)}</span>
                                        </td>
                                        <td className="lists-td text-right" onClick={e => e.stopPropagation()}>
                                            <button className="lists-icon-btn danger" title="Delete entry"
                                                onClick={() => handleDeleteEntry(entry.id)}>
                                                <span className="material-symbols-outlined">delete</span>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="lists-table-footer">
                    <span>Displaying {entries.length} of {total} entries</span>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[50, 100, 250, 500]}
                        sx={{ border: 'none', '& .MuiToolbar-root': { minHeight: 0, padding: 0 } }}
                    />
                </div>
            </div>

            {/* ── Add Entry Modal ────────────────────────────────── */}
            {addOpen && (
                <div className="lists-modal-overlay" onClick={(event) => {
                    if (event.target === event.currentTarget) setAddOpen(false);
                }}>
                    <div
                        ref={addModalRef}
                        className="lists-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={addTitleId}
                        tabIndex={-1}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="lists-modal-header">
                            <h3 id={addTitleId} className="lists-modal-title">Add IP Entry</h3>
                            <button type="button" className="lists-icon-btn" onClick={() => setAddOpen(false)} aria-label="Close Add IP Entry">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="lists-modal-content">
                            {addError && (
                                <div className="lists-error-banner lists-error-banner-no-margin">
                                    <span className="material-symbols-outlined text-sm">error</span>{addError}
                                </div>
                            )}
                            <div className="lists-filter-select-wrap lists-full-width">
                                <label className="lists-filter-label" htmlFor={addIpId}>IP Address / CIDR *</label>
                                <input id={addIpId} className="lists-native-input" placeholder="e.g. 10.0.0.0/8"
                                    value={newIp} autoFocus onChange={e => setNewIp(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && !adding && newIp.trim() && handleAddEntry()} />
                            </div>
                            <div className="lists-filter-select-wrap lists-full-width">
                                <label className="lists-filter-label" htmlFor={addMetaId}>Metadata (JSON, optional)</label>
                                <textarea id={addMetaId} className="lists-native-input" rows={2}
                                    placeholder='{"note": "manually added"}'
                                    value={newMeta} onChange={e => setNewMeta(e.target.value)} />
                            </div>
                        </div>
                        <div className="lists-modal-footer">
                            <button type="button" className="lists-modal-cancel" onClick={() => setAddOpen(false)}>Cancel</button>
                            <button type="button" className="lists-modal-confirm" onClick={handleAddEntry}
                                disabled={adding || !newIp.trim()}>
                                {adding ? 'Adding…' : 'Add Entry'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Bulk Add Modal ─────────────────────────────────── */}
            {bulkOpen && (
                <div className="lists-modal-overlay" onClick={(event) => {
                    if (event.target === event.currentTarget) setBulkOpen(false);
                }}>
                    <div
                        ref={bulkModalRef}
                        className="lists-modal lists-modal-wide"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={bulkTitleId}
                        tabIndex={-1}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="lists-modal-header">
                            <h3 id={bulkTitleId} className="lists-modal-title">Bulk Add IP Entries</h3>
                            <button type="button" className="lists-icon-btn" onClick={() => setBulkOpen(false)} aria-label="Close Bulk Add IP Entries">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="lists-modal-content">
                            {bulkError && (
                                <div className="lists-error-banner lists-error-banner-no-margin">
                                    <span className="material-symbols-outlined text-sm">error</span>{bulkError}
                                </div>
                            )}
                            <p className="lists-tab-desc">Enter one IP address or CIDR per line.</p>
                            <div className="lists-filter-select-wrap lists-full-width">
                                <label className="lists-filter-label" htmlFor={bulkIpsId}>IP Addresses *</label>
                                <textarea id={bulkIpsId} className="lists-native-input lists-native-input-mono" rows={8} autoFocus
                                    placeholder={"10.0.0.0/8\n192.168.1.0/24\n1.2.3.4"}
                                    value={bulkIps} onChange={e => setBulkIps(e.target.value)} />
                            </div>
                        </div>
                        <div className="lists-modal-footer">
                            <button type="button" className="lists-modal-cancel" onClick={() => setBulkOpen(false)}>Cancel</button>
                            <button type="button" className="lists-modal-confirm" onClick={handleBulkAdd}
                                disabled={bulkAdding || !bulkIps.trim()}>
                                {bulkAdding ? 'Adding…' : 'Add All'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
